import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useTaskStore } from '@/store/useTaskStore'
import { useAuthStore } from '@/store/useAuthStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const emptyToUndefined = (value: unknown) => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string' && value.trim() === '') return undefined
  return value
}

const optionalRuntimeNumber = z.preprocess(
  emptyToUndefined,
  z.coerce.number().optional()
)

const optionalThresholdNumber = z.preprocess(
  emptyToUndefined,
  z.coerce.number().min(0).max(100).optional()
)

const taskSchema = z.object({
  name: z.string().min(1, 'Task name required'),
  client_id: z.string().min(1, 'Client required'),
  motivation: z.string().min(1, 'Motivation required'),
  sales_nav_url: z.string().optional(),
  blacklist: z.string(),
  example_profiles: z.string(),
  runtime: z.object({
    maxSalesNavResults: optionalRuntimeNumber,
    maxPostsPerProfile: optionalRuntimeNumber,
    maxEngagerPosts: optionalRuntimeNumber,
    maxEngagers: optionalRuntimeNumber,
    defaultScoreThreshold: optionalThresholdNumber,
  }),
  include_post_analysis: z.boolean(),
  visible_to_all: z.boolean(),
})

type TaskFormData = z.infer<typeof taskSchema>

export function TaskForm() {
  const queryClient = useQueryClient()
  const { selectedTaskId, tasks, selectTask } = useTaskStore()
  const { user } = useAuthStore()
  const selectedTask = tasks.find((t) => t.id === selectedTaskId)
  const [isExpanded, setIsExpanded] = useState(false)
  const isAdmin = user?.role === 'admin'
  const canManageSelectedTask = !selectedTask
    || isAdmin
    || selectedTask.created_by === null
    || selectedTask.created_by === undefined
    || selectedTask.created_by === user?.id

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: '',
      client_id: '',
      motivation: '',
      sales_nav_url: '',
      blacklist: '',
      example_profiles: '',
      runtime: {
        maxSalesNavResults: undefined,
        maxPostsPerProfile: undefined,
        maxEngagerPosts: undefined,
        maxEngagers: undefined,
        defaultScoreThreshold: undefined,
      },
      include_post_analysis: false,
      visible_to_all: false,
    },
  })

  const visibleToAll = watch('visible_to_all')

  useEffect(() => {
    if (selectedTask) {
      reset({
        name: selectedTask.name,
        client_id: selectedTask.client_id,
        motivation: selectedTask.motivation,
        sales_nav_url: selectedTask.sales_nav_url || '',
        blacklist: selectedTask.blacklist.join(', '),
        example_profiles: selectedTask.example_profiles.join('\n'),
        runtime: selectedTask.runtime,
        include_post_analysis: selectedTask.include_post_analysis,
        visible_to_all: selectedTask.visible_to_all === true,
      })
    } else {
      reset({
        name: '',
        client_id: '',
        motivation: '',
        sales_nav_url: '',
        blacklist: '',
        example_profiles: '',
        runtime: {},
        include_post_analysis: false,
        visible_to_all: false,
      })
    }
  }, [selectedTask, reset])

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createTask(data),
    onSuccess: (newTask) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      selectTask(newTask.id)
      toast.success('Task created successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to create task: ${error.message}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.updateTask(selectedTaskId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Task updated successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to update task: ${error.message}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteTask(selectedTaskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      selectTask(null)
      toast.success('Task deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete task: ${error.message}`)
    },
  })

  const onSubmit = (data: TaskFormData) => {
    if (selectedTaskId && !canManageSelectedTask) return

    const cleanedRuntime = Object.fromEntries(
      Object.entries(data.runtime || {}).filter(([, value]) => value !== undefined)
    )

    const payload = {
      ...data,
      runtime: cleanedRuntime,
      blacklist: data.blacklist
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      example_profiles: data.example_profiles
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      visible_to_all: isAdmin ? data.visible_to_all : false,
    }

    if (selectedTaskId) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-900"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
          <CardTitle>
            {selectedTaskId ? (canManageSelectedTask ? 'Edit Task' : 'View Task') : 'New Task'}
          </CardTitle>
          {selectedTask && !isExpanded && (
            <span className="ml-auto text-xs text-slate-500">{selectedTask.name}</span>
          )}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name">Task Name</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Enterprise outreach - Netherlands"
                className="mt-1"
              />
              {errors.name && (
                <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="client_id">Client</Label>
              <Select id="client_id" {...register('client_id')} className="mt-1">
                <option value="">Select client</option>
                {clientsData?.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              {errors.client_id && (
                <p className="text-xs text-red-400 mt-1">{errors.client_id.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="sales_nav_url">Sales Navigator URL (optional)</Label>
            <Input
              id="sales_nav_url"
              {...register('sales_nav_url')}
              placeholder="https://www.linkedin.com/sales/search/people?..."
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="motivation">Motivation</Label>
            <Textarea
              id="motivation"
              {...register('motivation')}
              rows={4}
              placeholder="French executives working in Netherlands tech companies..."
              className="mt-1"
            />
            {errors.motivation && (
              <p className="text-xs text-red-400 mt-1">{errors.motivation.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="blacklist">Blacklist Terms (comma-separated)</Label>
              <Input
                id="blacklist"
                {...register('blacklist')}
                placeholder="Intern, Recruiter, Student"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="example_profiles">Example Profiles (one per line)</Label>
              <Textarea
                id="example_profiles"
                {...register('example_profiles')}
                rows={3}
                placeholder="https://linkedin.com/in/example1&#10;https://linkedin.com/in/example2"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Runtime Controls (optional)</Label>
              <span className="text-xs text-slate-500">Defaults: 100, 10, 5, 50, 0</span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              <div>
                <Label htmlFor="maxSalesNavResults" className="text-xs text-slate-400">
                  Sales Nav
                </Label>
                <Input
                  id="maxSalesNavResults"
                  type="number"
                  {...register('runtime.maxSalesNavResults')}
                  placeholder="100"
                  className="text-sm h-8"
                />
              </div>
              <div>
                <Label htmlFor="maxPostsPerProfile" className="text-xs text-slate-400">
                  Posts/Profile
                </Label>
                <Input
                  id="maxPostsPerProfile"
                  type="number"
                  {...register('runtime.maxPostsPerProfile')}
                  placeholder="10"
                  className="text-sm h-8"
                />
              </div>
              <div>
                <Label htmlFor="maxEngagerPosts" className="text-xs text-slate-400">
                  Eng. Posts
                </Label>
                <Input
                  id="maxEngagerPosts"
                  type="number"
                  {...register('runtime.maxEngagerPosts')}
                  placeholder="5"
                  className="text-sm h-8"
                />
              </div>
              <div>
                <Label htmlFor="maxEngagers" className="text-xs text-slate-400">
                  Max Eng.
                </Label>
                <Input
                  id="maxEngagers"
                  type="number"
                  {...register('runtime.maxEngagers')}
                  placeholder="50"
                  className="text-sm h-8"
                />
              </div>
              <div>
                <Label htmlFor="defaultScoreThreshold" className="text-xs text-slate-400">
                  Threshold
                </Label>
                <Input
                  id="defaultScoreThreshold"
                  type="number"
                  {...register('runtime.defaultScoreThreshold')}
                  placeholder="0"
                  className="text-sm h-8"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="include_post_analysis" {...register('include_post_analysis')} />
            <Label htmlFor="include_post_analysis" className="cursor-pointer">
              Include Post Analysis
            </Label>
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2">
              <Checkbox id="visible_to_all" {...register('visible_to_all')} />
              <Label htmlFor="visible_to_all" className="cursor-pointer">
                Visible to all: {visibleToAll ? 'true' : 'false'}
              </Label>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-800">
            <Button
              type="submit"
              disabled={!canManageSelectedTask || createMutation.isPending || updateMutation.isPending}
            >
              {selectedTaskId ? 'Update Task' : 'Create Task'}
            </Button>
            {selectedTaskId && canManageSelectedTask && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                Delete Task
              </Button>
            )}
            {!selectedTaskId && (
              <Button type="button" variant="outline" onClick={() => reset()}>
                Clear
              </Button>
            )}
          </div>
        </form>
      </CardContent>
      )}
    </Card>
  )
}
